import React from 'react';
import { EvaluationItem } from '../../../../types/recruiter';

interface GenericSkillsSectionProps {
  skills: {
    skill_1?: EvaluationItem;
    skill_2?: EvaluationItem;
    skill_3?: EvaluationItem;
    total?: number;
  };
  totals: { total: number; max: number };
}

export const GenericSkillsSection: React.FC<GenericSkillsSectionProps> = ({
  skills,
  totals,
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-4">汎用スキル</h4>
      <div className="space-y-4">
        {/* skill_1 (論理的思考力) */}
        {skills.skill_1 && (
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-medium text-gray-900">
                {skills.skill_1.title || '論理的思考力'}
              </h5>
              <span className="text-sm font-semibold text-gray-900">
                {skills.skill_1.score ?? 0}~/{skills.skill_1.maxScore ?? 0}点
              </span>
            </div>
            {skills.skill_1.criteriaItems && Array.isArray(skills.skill_1.criteriaItems) && (
              <details className="mt-2">
                <summary className="text-xs text-gray-600 cursor-pointer">評価項目</summary>
                <ol className="list-decimal ml-5 text-xs text-gray-700 mt-1">
                  {skills.skill_1.criteriaItems.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ol>
              </details>
            )}
            {skills.skill_1.evaluation && (
              <div className="text-sm text-gray-700 mt-2 whitespace-pre-line">
                <span className="font-medium">【総合評価】</span>{skills.skill_1.evaluation}
              </div>
            )}
            {skills.skill_1.criteriaItems && Array.isArray(skills.skill_1.criteriaItems) && skills.skill_1.evaluations && (
              <div className="mt-2 space-y-2">
                {skills.skill_1.criteriaItems.map((_, idx) =>
                  skills.skill_1!.evaluations![idx] ? (
                    <div key={idx} className="text-sm text-gray-700 whitespace-pre-line">
                      <span className="font-medium">【評価項目{idx + 1}】</span>{skills.skill_1!.evaluations![idx]}
                    </div>
                  ) : null
                )}
              </div>
            )}
          </div>
        )}

        {/* skill_2 (数理的思考力) */}
        {skills.skill_2 && (
          <div className="bg-indigo-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-medium text-gray-900">
                {skills.skill_2.title || '数理的思考力'}
              </h5>
              <span className="text-sm font-semibold text-gray-900">
                {skills.skill_2.score ?? 0}~/{skills.skill_2.maxScore ?? 0}点
              </span>
            </div>
            {skills.skill_2.criteriaItems && Array.isArray(skills.skill_2.criteriaItems) && (
              <details className="mt-2">
                <summary className="text-xs text-gray-600 cursor-pointer">評価項目</summary>
                <ol className="list-decimal ml-5 text-xs text-gray-700 mt-1">
                  {skills.skill_2.criteriaItems.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ol>
              </details>
            )}
            {skills.skill_2.evaluation && (
              <div className="text-sm text-gray-700 mt-2 whitespace-pre-line">
                <span className="font-medium">【総合評価】</span>{skills.skill_2.evaluation}
              </div>
            )}
            {skills.skill_2.criteriaItems && Array.isArray(skills.skill_2.criteriaItems) && skills.skill_2.evaluations && (
              <div className="mt-2 space-y-2">
                {skills.skill_2.criteriaItems.map((_, idx) =>
                  skills.skill_2!.evaluations![idx] ? (
                    <div key={idx} className="text-sm text-gray-700 whitespace-pre-line">
                      <span className="font-medium">【評価項目{idx + 1}】</span>{skills.skill_2!.evaluations![idx]}
                    </div>
                  ) : null
                )}
              </div>
            )}
          </div>
        )}

        {/* skill_3 (問題解決能力) */}
        {skills.skill_3 && (
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-medium text-gray-900">
                {skills.skill_3.title || '問題解決能力'}
              </h5>
              <span className="text-sm font-semibold text-gray-900">
                {skills.skill_3.score ?? 0}~/{skills.skill_3.maxScore ?? 0}点
              </span>
            </div>
            {skills.skill_3.criteriaItems && Array.isArray(skills.skill_3.criteriaItems) && (
              <details className="mt-2">
                <summary className="text-xs text-gray-600 cursor-pointer">評価項目</summary>
                <ol className="list-decimal ml-5 text-xs text-gray-700 mt-1">
                  {skills.skill_3.criteriaItems.map((it, idx) => (
                    <li key={idx}>{it}</li>
                  ))}
                </ol>
              </details>
            )}
            {skills.skill_3.evaluation && (
              <div className="text-sm text-gray-700 mt-2 whitespace-pre-line">
                <span className="font-medium">【総合評価】</span>{skills.skill_3.evaluation}
              </div>
            )}
            {skills.skill_3.criteriaItems && Array.isArray(skills.skill_3.criteriaItems) && skills.skill_3.evaluations && (
              <div className="mt-2 space-y-2">
                {skills.skill_3.criteriaItems.map((_, idx) =>
                  skills.skill_3!.evaluations![idx] ? (
                    <div key={idx} className="text-sm text-gray-700 whitespace-pre-line">
                      <span className="font-medium">【評価項目{idx + 1}】</span>{skills.skill_3!.evaluations![idx]}
                    </div>
                  ) : null
                )}
              </div>
            )}
          </div>
        )}

        {/* 汎用スキル合計 */}
        <div className="pt-2 border-t border-gray-200">
          <div className="flex justify-between items-center text-lg font-semibold">
            <span>汎用スキル合計</span>
            <span className="text-purple-600">
              {totals.total}~/{totals.max}点
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
